import React, { useState, useRef, useEffect, type CSSProperties } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, Text, Platform } from 'react-native';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { GOOGLE_PLACES_API_KEY } from '@env';

interface AddressPlaceDetails {
  place_id: string;
  formatted_address?: string;
  geometry?: unknown;
  name?: string;
  types?: string[];
}

interface AddressAutocompleteProps {
  value: string;
  onAddressChange: (address: string, details?: AddressPlaceDetails) => void;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
}

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlacePredictionRequest {
  input: string;
  types: string[];
  componentRestrictions: {
    country: string;
  };
}

interface PlaceDetailsRequest {
  placeId: string;
  fields: string[];
}

interface PlaceDetailsResult {
  formatted_address?: string;
  geometry?: unknown;
  name?: string;
  types?: string[];
}

interface GoogleAutocompleteService {
  getPlacePredictions: (
    request: PlacePredictionRequest,
    callback: (predictions: PlacePrediction[] | null, status: string) => void
  ) => void;
}

interface GooglePlacesService {
  getDetails: (
    request: PlaceDetailsRequest,
    callback: (place: PlaceDetailsResult | null, status: string) => void
  ) => void;
}

interface GooglePlacesApi {
  AutocompleteService: new () => GoogleAutocompleteService;
  PlacesService: new (container: HTMLElement) => GooglePlacesService;
}

interface GoogleMapsApi {
  maps: {
    places: GooglePlacesApi;
  };
}

type GoogleWindow = Window & {
  google?: GoogleMapsApi;
};

type WebViewStyle = ViewStyle & {
  boxShadow?: CSSProperties['boxShadow'];
  boxSizing?: CSSProperties['boxSizing'];
  cursor?: CSSProperties['cursor'];
  overflowY?: CSSProperties['overflowY'];
};

type WebTextStyle = TextStyle & {
  overflow?: CSSProperties['overflow'];
  textOverflow?: CSSProperties['textOverflow'];
  whiteSpace?: CSSProperties['whiteSpace'];
};

const asViewStyle = (style: WebViewStyle): ViewStyle => style;
const asTextStyle = (style: WebTextStyle): TextStyle => style;

const getGooglePlacesApi = (): GooglePlacesApi | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as GoogleWindow).google?.maps.places;
};

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onAddressChange,
  placeholder = "Enter property address",
  style
}) => {
  const [query, setQuery] = useState(value);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Initialize Google Places Autocomplete Service
  const autocompleteService = useRef<GoogleAutocompleteService | null>(null);
  const placesService = useRef<GooglePlacesService | null>(null);

  useEffect(() => {
    const googlePlaces = getGooglePlacesApi();
    if (googlePlaces && typeof document !== 'undefined') {
      autocompleteService.current = new googlePlaces.AutocompleteService();
      placesService.current = new googlePlaces.PlacesService(
        document.createElement('div')
      );
    }
  }, []);

  // Load Google Maps API if not already loaded
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      !getGooglePlacesApi()
    ) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        const googlePlaces = getGooglePlacesApi();
        if (!googlePlaces) {
          return;
        }

        autocompleteService.current = new googlePlaces.AutocompleteService();
        placesService.current = new googlePlaces.PlacesService(
          document.createElement('div')
        );
      };
      document.head.appendChild(script);
    }
  }, []);

  const searchPlaces = (input: string) => {
    if (!autocompleteService.current || input.length < 2) {
      setPredictions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);

    const request: PlacePredictionRequest = {
      input,
      types: ['address'],
      componentRestrictions: { country: 'us' }
    };

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setIsLoading(false);
      if (status === 'OK' && predictions) {
        setPredictions(predictions);
        setShowSuggestions(true);
      } else {
        setPredictions([]);
        setShowSuggestions(false);
      }
    });
  };

  const handleTextChange = (text: string) => {
    setQuery(text);
    onAddressChange(text);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      searchPlaces(text);
    }, 300);
  };

  const handleSuggestionPress = (prediction: PlacePrediction) => {
    setQuery(prediction.description);
    setShowSuggestions(false);
    setPredictions([]);

    // Get place details
    if (placesService.current) {
      const request: PlaceDetailsRequest = {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types']
      };

      placesService.current.getDetails(request, (place, status) => {
        if (status === 'OK' && place) {
          onAddressChange(prediction.description, {
            place_id: prediction.place_id,
            formatted_address: place.formatted_address,
            geometry: place.geometry,
            name: place.name,
            types: place.types
          });
        } else {
          onAddressChange(prediction.description, { place_id: prediction.place_id });
        }
      });
    } else {
      onAddressChange(prediction.description, { place_id: prediction.place_id });
    }
  };

  return (
    <View style={[styles.container, style]}>
      <TextInput
        style={styles.textInput}
        placeholder={placeholder}
        value={query}
        onChangeText={handleTextChange}
        onFocus={() => {
          if (predictions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          // Delay hiding suggestions to allow selection
          setTimeout(() => setShowSuggestions(false), 200);
        }}
      />
      
      {showSuggestions && predictions.length > 0 && (
        <View style={styles.suggestionsList}>
          {predictions.map((prediction, index) => (
            <TouchableOpacity
              key={prediction.place_id}
              style={[
                styles.suggestionItem,
                index === predictions.length - 1 && styles.lastSuggestionItem
              ]}
              onPress={() => handleSuggestionPress(prediction)}
            >
              <Text style={styles.suggestionMainText}>
                {prediction.structured_formatting.main_text}
              </Text>
              <Text style={styles.suggestionSecondaryText}>
                {prediction.structured_formatting.secondary_text}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      
      {isLoading && (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading suggestions...</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    zIndex: 1000,
  },
  textInput: {
    height: 48,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  suggestionsList: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderTopWidth: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    maxHeight: 200,
    zIndex: 1001,
    // Web-specific shadow and overflow
    ...(Platform.OS === 'web' ? asViewStyle({
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      overflowY: 'auto',
    }) : {}),
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    width: '100%',
    ...(Platform.OS === 'web' ? asViewStyle({
      cursor: 'pointer',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }) : {}),
  },
  lastSuggestionItem: {
    borderBottomWidth: 0,
  },
  suggestionMainText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
    ...(Platform.OS === 'web' ? asTextStyle({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }) : {}),
  },
  suggestionSecondaryText: {
    fontSize: 13,
    color: '#666',
    ...(Platform.OS === 'web' ? asTextStyle({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }) : {}),
  },
  loadingContainer: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderTopWidth: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: 12,
    zIndex: 1001,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
});

export default AddressAutocomplete;
